/**
 * Website Monitor Component
 * Monitor website performance, uptime, and user analytics.
 * Executive-grade dashboard using shared monitoring design system.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Web as WebIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  Public as PublicIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  InfoOutlined as InfoIcon,
  Security as SecurityIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import { MonitoringChart } from '../charts/ChartComponents';
import {
  getMonitoringColors,
  mapStatus,
  monoValueSx,
  serviceRowSx,
  criticalPulse,
} from './monitoringDesignSystem';
import {
  MonitoringPageContainer,
  MonitoringHeroBanner,
  MonitoringKPICard,
  MonitoringChartCard,
  UptimeSparkbar,
  AutoRefreshIndicator,
} from './MonitoringComponents';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { HOUR_PRESETS, HourToken } from '../common/timeRange';

// ---------------------------------------------------------------------------
// Interfaces (preserved)
// ---------------------------------------------------------------------------

interface SSLCertificate {
  domain: string;
  status: string;
  daysToExpiry: number;
  notAfter: string;
  region: string;
  isWildcard: boolean;
}

interface WebsiteMetrics {
  uptime: number;
  responseTime: number;
  loadTime: number;
  availability: number;
  sslStatus: 'valid' | 'expiring' | 'expired';
  sslDaysUntilExpiry: number;
  sslCertificates?: SSLCertificate[];
}

interface PerformanceMetrics {
  pageViews: number;
  uniqueVisitors: number;
  bounceRate: number;
  avgSessionDuration: number;
  conversionRate: number;
  mobileTraffic: number;
}

interface EndpointStatus {
  endpoint: string;
  status: 'up' | 'down' | 'slow';
  responseTime: number;
  lastChecked: string;
  statusCode: number;
}

interface GeographicData {
  country: string;
  visitors: number;
  percentage: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WebsiteMonitor: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);
  const dispatch = useDispatch<AppDispatch>();

  // --- State (preserved) ---
  const [websiteMetrics, setWebsiteMetrics] = useState<WebsiteMetrics | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [endpoints, setEndpoints] = useState<EndpointStatus[]>([]);
  const [geographicData, setGeographicData] = useState<GeographicData[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  // Hour-scale window: the SAME pill control as every other screen, over this
  // screen's own vocabulary. `resolveRange()` speaks calendar days only, so the
  // token is handed to the existing fetch logic untouched.
  const [timeRange, setTimeRange] = useTimeRangeParam<HourToken>(
    { type: '24h' },
    { presets: HOUR_PRESETS }
  );
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // --- Data loading (preserved) ---
  const loadWebsiteData = async () => {
    try {
      setLoading(true);
      const [metricsResponse, performanceResponse, endpointsResponse, geoResponse, chartResponse] = await Promise.all([
        apiService.getWebsiteMetrics(),
        apiService.getWebsitePerformance(timeRange.type),
        apiService.getEndpointStatus(),
        apiService.getGeographicData(timeRange.type),
        apiService.getWebsiteChart(timeRange.type),
      ]);

      setWebsiteMetrics((metricsResponse as any).data || metricsResponse);
      setPerformanceMetrics((performanceResponse as any).data || performanceResponse);
      setEndpoints((endpointsResponse as any).data || endpointsResponse || []);
      setGeographicData((geoResponse as any).data || geoResponse || []);
      setChartData((chartResponse as any).data || chartResponse);
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: 'Failed to load website monitoring data' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebsiteData();
  }, [timeRange]);

  useEffect(() => {
    const interval = setInterval(loadWebsiteData, 60000);
    return () => clearInterval(interval);
  }, [timeRange]);

  // --- Utility functions (preserved) ---
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const hasTrafficData = performanceMetrics && (performanceMetrics.pageViews > 0 || performanceMetrics.uniqueVisitors > 0);
  const hasChartData = chartData && chartData.labels?.length > 0 && chartData.datasets?.some((ds: any) => ds.data?.some((v: number) => v > 0));

  // --- Computed ---
  const getOverallHealth = (): 'healthy' | 'warning' | 'critical' => {
    if (!websiteMetrics) return 'healthy';
    if (websiteMetrics.sslStatus === 'expired' || websiteMetrics.uptime < 99) return 'critical';
    if (websiteMetrics.sslStatus === 'expiring' || websiteMetrics.responseTime > 500 || websiteMetrics.uptime < 99.5) return 'warning';
    return 'healthy';
  };

  const overallHealth = getOverallHealth();
  const bannerLabel =
    overallHealth === 'healthy' ? 'WEBSITE OPERATIONAL' :
    overallHealth === 'warning' ? 'WEBSITE DEGRADED' : 'WEBSITE DOWN';

  const bannerParts: string[] = [];
  if (websiteMetrics) {
    bannerParts.push(`${websiteMetrics.uptime.toFixed(2)}% uptime`);
    bannerParts.push(`${websiteMetrics.responseTime}ms response`);
    if (websiteMetrics.sslStatus === 'valid') bannerParts.push(`SSL valid (${websiteMetrics.sslDaysUntilExpiry} days)`);
    else if (websiteMetrics.sslStatus === 'expiring') bannerParts.push(`SSL expiring (${websiteMetrics.sslDaysUntilExpiry} days)`);
    else bannerParts.push('SSL expired');
  }
  const bannerSummary = bannerParts.join(' \u00b7 ');

  const getUptimeColor = (uptime: number): string => {
    if (uptime >= 99.9) return c.healthy;
    if (uptime >= 99.0) return c.warning;
    return c.critical;
  };

  const getResponseColor = (ms: number): string => {
    if (ms < 300) return c.healthy;
    if (ms < 500) return c.warning;
    return c.critical;
  };

  const getLoadTimeColor = (seconds: number): string => {
    if (seconds < 2) return c.healthy;
    if (seconds < 3) return c.warning;
    return c.critical;
  };

  // Build sparkbar days from uptime
  const buildSparkbarDays = () => {
    if (!websiteMetrics) return [];
    return Array.from({ length: 90 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (89 - i));
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const uptimeVal = websiteMetrics.uptime;
      const dayHealth = uptimeVal >= 99.9 ? 'operational' :
        uptimeVal >= 99.0 && i < Math.floor((100 - uptimeVal) * 0.9) ? 'degraded' :
        uptimeVal < 99.0 && i < Math.floor((100 - uptimeVal) * 0.9) ? 'outage' : 'operational';
      return { date: dateStr, status: dayHealth as 'operational' | 'degraded' | 'outage' };
    });
  };

  return (
    <MonitoringPageContainer>
      {/* SSL Alert — only show for actual expired/expiring certs */}
      {websiteMetrics && (websiteMetrics.sslStatus === 'expired' || websiteMetrics.sslStatus === 'expiring') && (
        <Alert
          severity={websiteMetrics.sslStatus === 'expired' ? 'error' : 'warning'}
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: websiteMetrics.sslStatus === 'expired' ? c.criticalBg : c.warningBg,
            ...(websiteMetrics.sslStatus === 'expired' ? criticalPulse : {}),
            ...(websiteMetrics.sslStatus === 'expired' ? { animation: 'criticalPulse 2s infinite' } : {}),
          }}
        >
          SSL Certificate {websiteMetrics.sslStatus === 'expired' ? 'has expired' : `expires in ${websiteMetrics.sslDaysUntilExpiry} days`}
        </Alert>
      )}

      {/* Hero Banner */}
      <MonitoringHeroBanner
        status={overallHealth}
        title={bannerLabel}
        subtitle={bannerSummary}
        actions={
          <>
            <TimeRangeFilter
              value={timeRange}
              onChange={setTimeRange}
              presets={HOUR_PRESETS}
              hideCustom
            />
            <AutoRefreshIndicator
              autoRefresh={autoRefresh}
              onToggle={() => setAutoRefresh(!autoRefresh)}
              onRefresh={loadWebsiteData}
            />
          </>
        }
      />

      {/* KPI Cards */}
      {websiteMetrics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={2}>
            <MonitoringKPICard
              label="Uptime"
              value={`${websiteMetrics.uptime.toFixed(2)}%`}
              accentColor={getUptimeColor(websiteMetrics.uptime)}
              icon={<TrendingUpIcon sx={{ fontSize: 32 }} />}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <MonitoringKPICard
              label="Response Time"
              value={`${websiteMetrics.responseTime}ms`}
              accentColor={getResponseColor(websiteMetrics.responseTime)}
              icon={<SpeedIcon sx={{ fontSize: 32 }} />}
              subtitle="Target: <500ms"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <MonitoringKPICard
              label="Load Time"
              value={`${websiteMetrics.loadTime.toFixed(1)}s`}
              accentColor={getLoadTimeColor(websiteMetrics.loadTime)}
              icon={<TimerIcon sx={{ fontSize: 32 }} />}
              subtitle="Target: <2s"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{
              bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
              borderRadius: 3,
              border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
              p: 2,
              height: '100%',
              display: 'flex',
              gap: 2,
            }}>
              {/* SSL Badge */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 100, borderRight: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`, pr: 2 }}>
                <SecurityIcon sx={{ fontSize: 28, color: websiteMetrics.sslStatus === 'expired' ? c.critical : websiteMetrics.sslStatus === 'expiring' ? c.warning : c.healthy, mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: c.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                  SSL Certificate
                </Typography>
                <Typography variant="h6" sx={{
                  ...monoValueSx,
                  color: websiteMetrics.sslStatus === 'expired' ? c.critical : websiteMetrics.sslStatus === 'expiring' ? c.warning : c.healthy,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                }}>
                  {websiteMetrics.sslStatus === 'valid' ? 'VALID' : websiteMetrics.sslStatus === 'expiring' ? 'EXPIRING' : 'EXPIRED'}
                </Typography>
              </Box>
              {/* Cert List */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'auto' }}>
                {websiteMetrics.sslCertificates && websiteMetrics.sslCertificates.length > 0 ? (
                  websiteMetrics.sslCertificates.map((cert, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {cert.daysToExpiry > 30 ? (
                          <CheckCircleIcon sx={{ color: c.healthy, fontSize: 14 }} />
                        ) : cert.daysToExpiry > 0 ? (
                          <WarningIcon sx={{ color: c.warning, fontSize: 14 }} />
                        ) : (
                          <ErrorIcon sx={{ color: c.critical, fontSize: 14 }} />
                        )}
                        <Typography variant="body2" sx={{ fontFamily: '"Fira Code", monospace', fontSize: '0.75rem', color: c.textPrimary }}>
                          {cert.domain}
                        </Typography>
                        {cert.isWildcard && (
                          <Chip label="wildcard" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{
                        ...monoValueSx,
                        fontSize: '0.75rem',
                        color: cert.daysToExpiry > 30 ? c.healthy : cert.daysToExpiry > 0 ? c.warning : c.critical,
                        whiteSpace: 'nowrap',
                        ml: 1,
                      }}>
                        {cert.daysToExpiry}d
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="caption" sx={{ color: c.textMuted }}>
                      {websiteMetrics.sslDaysUntilExpiry > 0 ? `${websiteMetrics.sslDaysUntilExpiry} days remaining (ACM auto-renew)` : 'Loading certificate details...'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* 90-Day Uptime Sparkbar */}
      {websiteMetrics && (
        <Box sx={{ mb: 3 }}>
          <MonitoringChartCard title="90-Day Uptime">
            <UptimeSparkbar days={buildSparkbarDays()} count={90} />
          </MonitoringChartCard>
        </Box>
      )}

      {/* SSL Certificate Details table removed — certs now shown inline next to SSL badge */}

      {/* Traffic Analytics + Key Metrics */}
      {performanceMetrics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <MonitoringChartCard title="Traffic Analytics">
              {hasTrafficData ? (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ ...monoValueSx, color: c.textPrimary }}>
                        {performanceMetrics.pageViews.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>Page Views</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ ...monoValueSx, color: c.textPrimary }}>
                        {performanceMetrics.uniqueVisitors.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>Unique Visitors</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h5"
                        sx={{
                          ...monoValueSx,
                          color: performanceMetrics.bounceRate > 70 ? c.critical : c.healthy,
                        }}
                      >
                        {performanceMetrics.bounceRate > 0 ? `${performanceMetrics.bounceRate.toFixed(1)}%` : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>Bounce Rate</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ ...monoValueSx, color: c.textPrimary }}>
                        {performanceMetrics.avgSessionDuration > 0 ? formatDuration(performanceMetrics.avgSessionDuration) : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>Avg Session</Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <InfoIcon sx={{ fontSize: 40, color: c.textMuted, mb: 1 }} />
                  <Typography variant="body1" sx={{ color: c.textSecondary }}>
                    No traffic data available for this period
                  </Typography>
                  <Typography variant="caption" sx={{ color: c.textMuted }}>
                    Data is sourced from CloudFront request metrics
                  </Typography>
                </Box>
              )}
            </MonitoringChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <MonitoringChartCard title="Key Metrics">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: c.textSecondary }}>Conversion Rate</Typography>
                  <Typography variant="h6" sx={{ ...monoValueSx, color: performanceMetrics.conversionRate > 0 ? c.healthy : c.textSecondary }}>
                    {performanceMetrics.conversionRate > 0 ? `${performanceMetrics.conversionRate.toFixed(2)}%` : 'N/A'}
                  </Typography>
                  {performanceMetrics.conversionRate === 0 && (
                    <Typography variant="caption" sx={{ color: c.textMuted }}>Requires analytics integration</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: c.textSecondary }}>Mobile Traffic</Typography>
                  <Typography variant="h6" sx={{ ...monoValueSx, color: performanceMetrics.mobileTraffic > 0 ? c.textPrimary : c.textSecondary }}>
                    {performanceMetrics.mobileTraffic > 0 ? `${performanceMetrics.mobileTraffic.toFixed(1)}%` : 'N/A'}
                  </Typography>
                  {performanceMetrics.mobileTraffic > 0 ? (
                    <LinearProgress
                      variant="determinate"
                      value={performanceMetrics.mobileTraffic}
                      sx={{
                        mt: 1,
                        height: 6,
                        borderRadius: 1,
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                      }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ color: c.textMuted }}>Requires analytics integration</Typography>
                  )}
                </Box>
              </Box>
            </MonitoringChartCard>
          </Grid>
        </Grid>
      )}

      {/* Performance Chart */}
      {chartData && (
        <Box sx={{ mb: 3 }}>
          {hasChartData ? (
            <MonitoringChartCard title="Website Performance Over Time" badge="live">
              <MonitoringChart
                data={chartData}
                title="Website Performance Over Time"
                height={300}
                type="line"
              />
            </MonitoringChartCard>
          ) : (
            <MonitoringChartCard title="Website Performance Over Time">
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <InfoIcon sx={{ fontSize: 48, color: c.textMuted, mb: 1 }} />
                <Typography variant="body1" sx={{ color: c.textSecondary }}>
                  No performance chart data available for this period
                </Typography>
                <Typography variant="caption" sx={{ color: c.textMuted }}>
                  Chart data is sourced from CloudFront request and error rate metrics
                </Typography>
              </Box>
            </MonitoringChartCard>
          )}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Endpoint Status */}
        <Grid item xs={12} lg={8}>
          <MonitoringChartCard title="Endpoint Status">
            {endpoints.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: c.textSecondary }}>Endpoint</TableCell>
                      <TableCell sx={{ color: c.textSecondary }}>Status</TableCell>
                      <TableCell align="right" sx={{ color: c.textSecondary }}>Response Time</TableCell>
                      <TableCell align="right" sx={{ color: c.textSecondary }}>Status Code</TableCell>
                      <TableCell sx={{ color: c.textSecondary }}>Last Checked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {endpoints.map((endpoint, index) => (
                      <TableRow key={index} sx={serviceRowSx(mapStatus(endpoint.status), mode)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {endpoint.status === 'up' ? (
                              <CheckCircleIcon sx={{ color: c.healthy, fontSize: 20 }} />
                            ) : endpoint.status === 'slow' ? (
                              <WarningIcon sx={{ color: c.warning, fontSize: 20 }} />
                            ) : (
                              <ErrorIcon sx={{ color: c.critical, fontSize: 20 }} />
                            )}
                            <Typography variant="body2" sx={{ fontFamily: '"Fira Code", monospace', color: c.textPrimary }}>
                              {endpoint.endpoint}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={endpoint.status.toUpperCase()}
                            color={endpoint.status === 'up' ? 'success' : endpoint.status === 'slow' ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{
                            ...monoValueSx,
                            color: endpoint.responseTime > 1000 ? c.critical :
                                   endpoint.responseTime > 500 ? c.warning : c.textPrimary,
                          }}>
                            {endpoint.responseTime}ms
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={endpoint.statusCode}
                            color={
                              endpoint.statusCode >= 200 && endpoint.statusCode < 300 ? 'success' :
                              endpoint.statusCode >= 400 ? 'error' : 'warning'
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: c.textMuted }}>
                            {new Date(endpoint.lastChecked).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <InfoIcon sx={{ fontSize: 40, color: c.textMuted, mb: 1 }} />
                <Typography variant="body1" sx={{ color: c.textSecondary }}>
                  No endpoint health checks found
                </Typography>
                <Typography variant="caption" sx={{ color: c.textMuted }}>
                  Endpoint data is sourced from direct HTTP health checks
                </Typography>
              </Box>
            )}
          </MonitoringChartCard>
        </Grid>

        {/* Geographic Data */}
        <Grid item xs={12} lg={4}>
          <MonitoringChartCard
            title="Visitor Geography"
            actions={<PublicIcon sx={{ color: c.textMuted }} />}
          >
            {geographicData.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {geographicData.slice(0, 8).map((country, index) => (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: c.textPrimary }}>{country.country}</Typography>
                      <Typography variant="body2" sx={{ ...monoValueSx, color: c.textPrimary }}>
                        {country.visitors.toLocaleString()} ({country.percentage.toFixed(1)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={country.percentage}
                      sx={{
                        height: 4,
                        borderRadius: 1,
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <PublicIcon sx={{ fontSize: 40, color: c.textMuted, mb: 1 }} />
                <Typography variant="subtitle2" sx={{ color: c.textPrimary, fontWeight: 600, mb: 0.5 }}>
                  No Geographic Data
                </Typography>
                <Typography variant="body2" sx={{ color: c.textSecondary, mb: 1 }}>
                  No visitor geography data available for this time period.
                </Typography>
                <Typography variant="caption" sx={{ color: c.textMuted }}>
                  Data is sourced from WAF sampled requests. Try selecting a longer time range.
                </Typography>
              </Box>
            )}
          </MonitoringChartCard>
        </Grid>
      </Grid>
    </MonitoringPageContainer>
  );
};

export default WebsiteMonitor;
